<template>
  <div class="pb-10">
    <PageTitle title="Factures Proforma" :description="`${total} facture(s) proforma au total`">
      <template #actions>
        <button
          class="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors"
          @click="handleCreateProforma">
          <i class="fas fa-plus text-xs"></i>
          Nouvelle proforma
        </button>
      </template>
    </PageTitle>

    <!-- Filtres -->
    <BaseCard class="mb-5">
      <div class="flex flex-wrap gap-4 items-end">
        <!-- Recherche -->
        <div class="flex-1 min-w-[250px]">
          <BaseInput
            v-model="search"
            type="search"
            placeholder="Rechercher une proforma (numero, client)..."
            clearable
            @input="handleSearch">
            <template #icon-left>
              <i class="fa-solid fa-search"></i>
            </template>
          </BaseInput>
        </div>

        <!-- Filtres de date -->
        <div class="flex gap-3">
          <div class="flex flex-col gap-1">
            <label for="dateFrom" class="text-xs font-medium text-gray-500">Du</label>
            <BaseInput id="dateFrom" v-model="filters.dateFrom" type="date" @change="loadProformaInvoices" />
          </div>
          <div class="flex flex-col gap-1">
            <label for="dateTo" class="text-xs font-medium text-gray-500">Au</label>
            <BaseInput id="dateTo" v-model="filters.dateTo" type="date" @change="loadProformaInvoices" />
          </div>
        </div>

        <!-- Filtre de statut -->
        <div class="flex flex-col gap-1">
          <label for="statusFilter" class="text-xs font-medium text-gray-500">Statut</label>
          <select
            id="statusFilter"
            v-model="filters.status"
            @change="loadProformaInvoices"
            class="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors min-w-[180px]"
          >
            <option value="">Tous les statuts</option>
            <option value="draft">Brouillon</option>
            <option value="accepted">Acceptee</option>
            <option value="rejected">Rejetee</option>
          </select>
        </div>
      </div>
    </BaseCard>

    <!-- Tableau des factures proforma -->
    <BaseCard padding="none">
      <!-- Chargement -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-16">
        <div class="w-8 h-8 border-[3px] border-gray-200 border-t-primary rounded-full animate-spin mb-3"></div>
        <p class="text-sm text-gray-400">Chargement des factures proforma...</p>
      </div>

      <!-- Etat vide -->
      <div v-else-if="proformaInvoices.length === 0" class="flex flex-col items-center justify-center py-16 text-center px-6">
        <div class="text-gray-300 mb-4">
          <i class="fa-solid fa-file-invoice text-5xl"></i>
        </div>
        <h3 class="text-base font-semibold text-gray-700 mb-1">Aucune facture proforma trouvee</h3>
        <p class="text-sm text-gray-400 max-w-sm mb-6">
          Cliquez sur "Nouvelle proforma" pour creer votre premiere facture proforma.
        </p>
        <button
          class="inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors"
          @click="handleCreateProforma">
          <i class="fas fa-plus text-xs"></i>
          Creer une proforma
        </button>
      </div>

      <!-- Tableau -->
      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="bg-gray-50/80 border-b border-gray-200">
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                N° Proforma
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Validite
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Client
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Montant
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Statut
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Cree par
              </th>
              <th class="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr
              v-for="proforma in proformaInvoices"
              :key="proforma.id"
              class="hover:bg-gray-50/80 transition-colors"
            >
              <td class="px-5 py-3.5">
                <span class="text-sm font-semibold text-gray-900 font-mono">{{ proforma.proforma_number }}</span>
              </td>
              <td class="px-5 py-3.5 text-sm text-gray-500">
                {{ formatDate(proforma.proforma_date) }}
              </td>
              <td class="px-5 py-3.5 text-sm text-gray-500">
                <span v-if="proforma.validity_date">{{ formatDate(proforma.validity_date) }}</span>
                <span v-else class="text-gray-300 italic">-</span>
              </td>
              <td class="px-5 py-3.5">
                <span v-if="proforma.customer && proforma.customer.name" class="text-sm text-gray-900 font-medium">
                  {{ proforma.customer.name }}
                </span>
                <span v-else class="text-sm text-gray-300 italic">-</span>
              </td>
              <td class="px-5 py-3.5">
                <span class="text-sm font-bold text-gray-900">{{ formatCurrency(proforma.total_incl_tax) }}</span>
              </td>
              <td class="px-5 py-3.5">
                <span :class="[
                  'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold',
                  getStatusClass(proforma.status)
                ]">
                  {{ getStatusLabel(proforma.status) }}
                </span>
              </td>
              <td class="px-5 py-3.5 text-sm text-gray-500">
                {{ proforma.user ? proforma.user.name : 'N/A' }}
              </td>
              <td class="px-5 py-3.5">
                <div class="flex items-center justify-end gap-1">
                  <button
                    class="p-1.5 text-gray-400 hover:text-primary hover:bg-primary-50 rounded-lg transition-colors"
                    title="Telecharger PDF"
                    :disabled="proforma.loading"
                    @click="handleDownloadPdf(proforma)">
                    <i v-if="proforma.loading" class="fas fa-spinner fa-spin text-sm"></i>
                    <i v-else class="fas fa-file-arrow-down text-sm"></i>
                  </button>
                  <router-link :to="{ name: 'proforma-invoice-details', params: { id: proforma.id } }">
                    <button class="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="Voir details">
                      <i class="fas fa-eye text-sm"></i>
                    </button>
                  </router-link>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="proformaInvoices.length > 0" class="border-t border-gray-100 px-5 py-4">
        <PaginationComponent
          :current-page="currentPage"
          :per-page="perPage"
          :total="total"
          :to="paginationInfo.to"
          :show-page-numbers="true"
          :max-visible-pages="5"
          @page-change="goToPage"
        />
      </div>
    </BaseCard>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { proformaInvoiceService } from '@/services'
import PageTitle from '@/components/common/PageTitle.vue'
import BaseCard from '@/components/base/BaseCard.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import PaginationComponent from '@/components/common/PaginationComponent.vue'

export default defineComponent({
  name: 'ProformaInvoicesView',
  components: {
    PageTitle,
    BaseCard,
    BaseInput,
    PaginationComponent
  },
  setup() {
    const router = useRouter()

    // Donnees d'etat
    const proformaInvoices = ref([])
    const loading = ref(true)
    const isDownloading = ref(false)
    const search = ref('')
    const filters = reactive({
      dateFrom: '',
      dateTo: '',
      status: ''
    })

    // Pagination
    const currentPage = ref(1)
    const lastPage = ref(1)
    const perPage = ref(15)
    const total = ref(0)
    const paginationTo = ref(0)
    const paginationFrom = ref(0)
    const searchTimeout = ref(null)

    // Calcul des informations de pagination
    const paginationInfo = computed(() => {
      return {
        from: paginationFrom.value,
        to: paginationTo.value,
        total: total.value
      }
    })

    // Methodes
    const loadProformaInvoices = async () => {
      try {
        loading.value = true

        const params = {
          page: currentPage.value,
          per_page: perPage.value,
          with: ['customer', 'user', 'sale']
        }

        if (search.value) {
          params.search = search.value
        }
        if (filters.dateFrom) {
          params.date_from = filters.dateFrom
        }
        if (filters.dateTo) {
          params.date_to = filters.dateTo
        }
        if (filters.status) {
          params.status = filters.status
        }

        const response = await proformaInvoiceService.getProformaInvoices(params)

        const responseData = response.data?.data || response.data || {}
        proformaInvoices.value = responseData.data || responseData || []

        proformaInvoices.value.forEach(proforma => {
          proforma.loading = false
        })

        const meta = responseData.meta || response.data?.meta || response.meta || {}
        total.value = meta.total || 0
        perPage.value = meta.per_page || 15
        paginationTo.value = meta.to || 0
        paginationFrom.value = meta.from || 0
        lastPage.value = Math.ceil(total.value / perPage.value) || 1

      } catch (error) {
        console.error('Erreur lors du chargement des factures proforma:', error)
      } finally {
        loading.value = false
      }
    }

    const handleSearch = () => {
      currentPage.value = 1
      clearTimeout(searchTimeout.value)
      searchTimeout.value = setTimeout(() => {
        loadProformaInvoices()
      }, 500)
    }

    const goToPage = (page) => {
      if (page < 1 || page > lastPage.value) return
      currentPage.value = page
      loadProformaInvoices()
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount || 0)
    }

    const formatDate = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }

    const getStatusLabel = (status) => {
      return proformaInvoiceService.getStatusLabel(status)
    }

    const getStatusClass = (status) => {
      const classes = {
        'draft': 'bg-gray-100 text-gray-600',
        'accepted': 'bg-green-50 text-green-700',
        'rejected': 'bg-red-50 text-red-700'
      }
      return classes[status] || 'bg-gray-100 text-gray-600'
    }

    // Actions
    const handleCreateProforma = () => {
      router.push({ name: 'create-proforma-invoice' })
    }

    const handleDownloadPdf = async (proforma) => {
      if (proforma.loading || isDownloading.value) return

      proforma.loading = true
      isDownloading.value = true

      try {
        await proformaInvoiceService.downloadPdf(proforma.id, true)
      } catch (error) {
        console.error('Erreur lors du telechargement du PDF:', error)
      } finally {
        proforma.loading = false
        isDownloading.value = false
      }
    }

    // Nettoyer le timeout lors du demontage
    onUnmounted(() => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value)
      }
    })

    // Charger les donnees au montage du composant
    onMounted(() => {
      loadProformaInvoices()
    })

    return {
      proformaInvoices,
      loading,
      search,
      filters,
      currentPage,
      lastPage,
      perPage,
      total,
      paginationInfo,
      isDownloading,
      loadProformaInvoices,
      handleSearch,
      goToPage,
      formatCurrency,
      formatDate,
      getStatusLabel,
      getStatusClass,
      handleCreateProforma,
      handleDownloadPdf
    }
  }
})
</script>
